export type FreemiumIntentId =
  | 'self-funding'
  | 'analyze-policy'
  | 'explore-quotes'
  | 'rate-action'
  | 'ccrc-offer'
  | 'comprehensive-plan'
  | 'something-else';

export const FREEMIUM_INTENT_ORDER: FreemiumIntentId[] = [
  'self-funding',
  'analyze-policy',
  'explore-quotes',
  'rate-action',
  'ccrc-offer',
  'comprehensive-plan',
  'something-else',
];

export type FreemiumTier = 'diy' | 'unlimited' | 'licensedFullService' | 'fullHandoff';
export type FreemiumPriceModel = 'one_time' | 'subscription' | 'free';
export type FreemiumTierOutcome = 'checkout' | 'handoff';

export interface FreemiumTierConfig {
  tier: FreemiumTier;
  name: string;
  priceModel: FreemiumPriceModel;
  priceUsd: number;
  priceSuffix?: string;
  bgaSponsorable?: boolean;
  recommended?: boolean;
  features: string[];
  ctaLabel: string;
  outcome: FreemiumTierOutcome;
  isLicensed?: boolean;
}

export const FREEMIUM_TIER_BILLING: Record<
  FreemiumTier,
  { priceModel: FreemiumPriceModel; priceUsd: number }
> = {
  diy: { priceModel: 'one_time', priceUsd: 49 },
  unlimited: { priceModel: 'subscription', priceUsd: 59 },
  licensedFullService: { priceModel: 'one_time', priceUsd: 129 },
  fullHandoff: { priceModel: 'free', priceUsd: 0 },
};

interface StripePriceEnvIds {
  dev: string;
  prod: string;
}

const FREEMIUM_STRIPE_PRICE_IDS: Partial<Record<FreemiumTier, StripePriceEnvIds>> = {
  diy: {
    dev: 'price_1Teg9ACrvzKtEHNdxTLHkVmU',
    prod: 'price_FREEMIUM_DIY_PROD_PLACEHOLDER',
  },
  unlimited: {
    dev: 'price_1Teg9xCrvzKtEHNdpJCXSYj2',
    prod: 'price_FREEMIUM_UNLIMITED_PROD_PLACEHOLDER',
  },
  licensedFullService: {
    dev: 'price_1TegAHCrvzKtEHNdMItSk1JZ',
    prod: 'price_FREEMIUM_LICENSED_FS_PROD_PLACEHOLDER',
  },
};

// Summary bullet used on the Unlimited card to point back at the Do it Yourself
// tier. On checkout we expand it into the actual Do it Yourself bullets.
const DIY_FULL_ACCESS_FEATURE =
  'Full access to everything in Do it Yourself for all of your clients';

const DIY_POLICY_FEATURES = [
  'Our AI extracts the premium and benefit details from your policy (upload or enter manually)',
  'The policy benefits are modeled against your LTC plan',
  'Automatically calculate ROI, CAGR, and IRR',
  'Instantly recalculate performance for different scenarios of interest',
  'Policy details map to your PDF exports for visualization',
];

const DIY_RATE_ACTION_FEATURES = [
  'Our AI extracts the option details from your rate action letter',
  'The options are modeled against your LTC plan',
  'ROI is calculated for each option',
  'Instantly recalculate for different scenarios of interest',
  'Option details map to your PDF exports for visualization',
];

const DIY_CCRC_FEATURES = [
  'Enter all of the details of the Type A, B, or C product offered by the CCRC',
  'The product benefits are modeled against your LTC plan',
  'Automatically calculate ROI, CAGR, and IRR',
  'Instantly recalculate performance for different scenarios of interest',
  'Product details map to your PDF exports for visualization',
];

const UNLIMITED_BASE_FEATURES = [
  DIY_FULL_ACCESS_FEATURE,
  '+Existing policy analysis',
  '+New policy analysis',
  '+Rate action analysis',
  '+Insurance quote optimization',
  '+AI underwriting',
  '+Policy application pre-fill',
  '+AI application NIGO checking',
  '+Automatic DocuSign signature routing',
  '+Policy application support',
];

const UNLIMITED_RATE_ACTION_FEATURES = [
  DIY_FULL_ACCESS_FEATURE,
  '+Existing policy analysis',
  '+New policy analysis',
  '+CCRC product analysis',
  '+Insurance quote optimization',
  '+AI underwriting',
  '+Policy application pre-fill',
  '+AI application NIGO checking',
  '+Automatic DocuSign signature routing',
  '+Policy application support',
];

function licensedFullServiceFeatures(subjectLabel = 'option'): string[] {
  return [
    `Comprehensive analysis of each ${subjectLabel} compared against varying scenarios.`,
    'Get a detailed PDF report explaining methodology, sources, and considerations, tailored to your planning preferences.',
    'Get a list of talking points and key highlights for guiding your conversation.',
    'Get an email-ready summary to go with the materials.',
    'Dedicated licensed support for your case to answer any questions as they arise.',
    'Availability for a live meeting with you and your client to walk through the plan.',
  ];
}

const FULL_HANDOFF_FEATURES = [
  'Full access to everything in Licensed Full Service.',
  'As many meetings, calls, and emails that your client needs to make an informed, no pressure decision.',
  'Commission split in the event a policy is placed.',
  'No fee if a policy is not placed.',
];

function diyTier(features: string[], bgaSponsorable = false): FreemiumTierConfig {
  return {
    tier: 'diy',
    name: 'Do it Yourself',
    priceModel: FREEMIUM_TIER_BILLING.diy.priceModel,
    priceUsd: FREEMIUM_TIER_BILLING.diy.priceUsd,
    bgaSponsorable,
    features,
    ctaLabel: 'Get started',
    outcome: 'checkout',
  };
}

function unlimitedTier(features: string[] = UNLIMITED_BASE_FEATURES): FreemiumTierConfig {
  return {
    tier: 'unlimited',
    name: 'Unlimited self-serve access',
    priceModel: FREEMIUM_TIER_BILLING.unlimited.priceModel,
    priceUsd: FREEMIUM_TIER_BILLING.unlimited.priceUsd,
    priceSuffix: '/month',
    recommended: true,
    features,
    ctaLabel: 'Subscribe',
    outcome: 'checkout',
  };
}

function licensedFullServiceTier(
  bgaSponsorable = false,
  subjectLabel = 'option',
): FreemiumTierConfig {
  return {
    tier: 'licensedFullService',
    name: 'Licensed Full Service',
    priceModel: FREEMIUM_TIER_BILLING.licensedFullService.priceModel,
    priceUsd: FREEMIUM_TIER_BILLING.licensedFullService.priceUsd,
    bgaSponsorable,
    isLicensed: true,
    features: licensedFullServiceFeatures(subjectLabel),
    ctaLabel: 'Get full service',
    outcome: 'checkout',
  };
}

function fullHandoffTier(): FreemiumTierConfig {
  return {
    tier: 'fullHandoff',
    name: 'Full Handoff',
    priceModel: FREEMIUM_TIER_BILLING.fullHandoff.priceModel,
    priceUsd: FREEMIUM_TIER_BILLING.fullHandoff.priceUsd,
    isLicensed: true,
    features: FULL_HANDOFF_FEATURES,
    ctaLabel: 'Hand it off to us',
    outcome: 'handoff',
  };
}

const PLACEMENT_TIERS: FreemiumTierConfig[] = [
  diyTier(DIY_POLICY_FEATURES, true),
  unlimitedTier(),
  licensedFullServiceTier(true),
  fullHandoffTier(),
];

const ANALYZE_IN_FORCE_TIERS: FreemiumTierConfig[] = [
  diyTier(DIY_POLICY_FEATURES),
  unlimitedTier(),
  licensedFullServiceTier(),
];

const RATE_ACTION_TIERS: FreemiumTierConfig[] = [
  diyTier(DIY_RATE_ACTION_FEATURES),
  unlimitedTier(UNLIMITED_RATE_ACTION_FEATURES),
  licensedFullServiceTier(),
];

const CCRC_TIERS: FreemiumTierConfig[] = [
  diyTier(DIY_CCRC_FEATURES),
  unlimitedTier(),
  licensedFullServiceTier(false, 'product option'),
];

export type FreemiumFlowKind =
  | 'paywall'
  | 'reflexive'
  | 'guidedPlanningDeferred'
  | 'consultHandoff';

export interface FreemiumReflexiveOption {
  id: string;
  label: string;
  description: string;
  tiers: FreemiumTierConfig[];
}

export interface FreemiumIntentFlow {
  intentId: FreemiumIntentId;
  label: string;
  description: string;
  kind: FreemiumFlowKind;
  tiers?: FreemiumTierConfig[];
  question?: { prompt: string; options: FreemiumReflexiveOption[] };
  freeTextPrompt?: string;
}

export const FREEMIUM_INTENT_FLOWS: Record<FreemiumIntentId, FreemiumIntentFlow> = {
  'self-funding': {
    intentId: 'self-funding',
    label: 'Evaluate self-funding',
    description: 'See whether paying out of pocket can cover future care.',
    kind: 'guidedPlanningDeferred',
    tiers: PLACEMENT_TIERS,
  },
  'analyze-policy': {
    intentId: 'analyze-policy',
    label: 'Analyze a policy',
    description: 'Review an existing policy’s coverage, gaps, and value.',
    kind: 'reflexive',
    question: {
      prompt:
        'Is this a policy that is already in force, or one that your client is considering purchasing?',
      options: [
        {
          id: 'in-force',
          label: 'In force',
          description: 'Your client already owns and is paying for this policy.',
          tiers: ANALYZE_IN_FORCE_TIERS,
        },
        {
          id: 'considering',
          label: 'Considering',
          description: 'Your client is evaluating whether to purchase this policy.',
          tiers: PLACEMENT_TIERS,
        },
      ],
    },
  },
  'explore-quotes': {
    intentId: 'explore-quotes',
    label: 'Explore quotes',
    description: 'Compare coverage and pricing across carriers.',
    kind: 'paywall',
    tiers: PLACEMENT_TIERS,
  },
  'rate-action': {
    intentId: 'rate-action',
    label: 'Navigate a policy rate action',
    description: 'Weigh the options on a premium increase and what to do next.',
    kind: 'paywall',
    tiers: RATE_ACTION_TIERS,
  },
  'ccrc-offer': {
    intentId: 'ccrc-offer',
    label: 'Evaluate a CCRC offer',
    description: 'Assess a continuing care community contract and costs.',
    kind: 'paywall',
    tiers: CCRC_TIERS,
  },
  'comprehensive-plan': {
    intentId: 'comprehensive-plan',
    label: 'Build a comprehensive financial plan for LTC',
    description: 'Build a complete long-term care funding strategy.',
    kind: 'consultHandoff',
    freeTextPrompt: 'What are the goals that you have for this strategy?',
  },
  'something-else': {
    intentId: 'something-else',
    label: 'Something else',
    description: 'Tell us what you have in mind and we’ll guide you.',
    kind: 'consultHandoff',
    freeTextPrompt: 'Tell us a bit more about what you have in mind.',
  },
};

export const FREEMIUM_INTENT_OPTIONS = FREEMIUM_INTENT_ORDER.map(id => ({
  id,
  label: FREEMIUM_INTENT_FLOWS[id].label,
  description: FREEMIUM_INTENT_FLOWS[id].description,
}));

export function getFreemiumIntentFlow(intentId: FreemiumIntentId): FreemiumIntentFlow {
  return FREEMIUM_INTENT_FLOWS[intentId];
}

export function resolveFreemiumTiers(
  intentId: FreemiumIntentId,
  reflexiveAnswer?: string | null,
): FreemiumTierConfig[] {
  const flow = FREEMIUM_INTENT_FLOWS[intentId];

  if (flow.kind === 'reflexive') {
    const option = flow.question?.options.find(o => o.id === reflexiveAnswer);
    return option?.tiers ?? [];
  }

  return flow.tiers ?? [];
}

export function resolveFreemiumTier(
  intentId: FreemiumIntentId,
  reflexiveAnswer: string | null | undefined,
  tier: FreemiumTier,
): FreemiumTierConfig | undefined {
  return resolveFreemiumTiers(intentId, reflexiveAnswer).find(t => t.tier === tier);
}

/**
 * Feature bullets to show on the checkout page for a selected tier.
 *
 * The Unlimited card summarizes its Do it Yourself coverage with a single
 * "Full access to everything in Do it Yourself" bullet, which reads as vague at
 * checkout. Here we expand that bullet into the actual Do it Yourself bullets so
 * the buyer sees exactly what they get. Other tiers are returned unchanged.
 */
export function getFreemiumCheckoutFeatures(
  intentId: FreemiumIntentId,
  reflexiveAnswer: string | null | undefined,
  tier: FreemiumTier,
): string[] {
  const config = resolveFreemiumTier(intentId, reflexiveAnswer, tier);
  if (!config) return [];
  if (tier !== 'unlimited') return config.features;

  const diy = resolveFreemiumTier(intentId, reflexiveAnswer, 'diy');
  if (!diy) return config.features;

  return config.features.flatMap(feature =>
    feature === DIY_FULL_ACCESS_FEATURE ? diy.features : [feature],
  );
}

export function isFreemiumTier(value: string): value is FreemiumTier {
  return (
    value === 'diy' ||
    value === 'unlimited' ||
    value === 'licensedFullService' ||
    value === 'fullHandoff'
  );
}

export function isFreemiumIntentId(value: string): value is FreemiumIntentId {
  return (FREEMIUM_INTENT_ORDER as string[]).includes(value);
}

export function getFreemiumTierBilling(tier: FreemiumTier) {
  return FREEMIUM_TIER_BILLING[tier];
}

export const FREEMIUM_TIER_META: Record<
  FreemiumTier,
  { name: string; isLicensed: boolean; outcome: FreemiumTierOutcome }
> = {
  diy: { name: 'Do it Yourself', isLicensed: false, outcome: 'checkout' },
  unlimited: { name: 'Unlimited self-serve access', isLicensed: false, outcome: 'checkout' },
  licensedFullService: { name: 'Licensed Full Service', isLicensed: true, outcome: 'checkout' },
  fullHandoff: { name: 'Full Handoff', isLicensed: true, outcome: 'handoff' },
};

export function getFreemiumTierMeta(tier: FreemiumTier) {
  return FREEMIUM_TIER_META[tier];
}

export function getFreemiumIntentLabel(intentId: FreemiumIntentId): string {
  return FREEMIUM_INTENT_FLOWS[intentId].label;
}

export function getFreemiumStripePriceId(tier: FreemiumTier, isProd: boolean): string {
  const ids = FREEMIUM_STRIPE_PRICE_IDS[tier];

  if (!ids) return '';

  return isProd ? ids.prod : ids.dev;
}

export const FREEMIUM_BGA_SPONSORED_TEXT = 'Free for advisors at partner BGAs';
export const FREEMIUM_BGA_SPONSORED_CTA = 'check if you’re eligible or get contracted';
export const FREEMIUM_BGA_SPONSORSHIP_URL = 'https://placeholder.waterlily.com/bga-eligibility';

export const FREEMIUM_ONBOARDING_CALL_URL =
  'https://calendly.com/d/cxdy-x3d-cqj/waterlily-onboarding-and-training';

export const WAG_SUPPORT_EMAIL = 'support@wealthassurancegroup.com';

export const FREEMIUM_TEAM_NOTIFICATION_EMAIL = 'support@joinwaterlily.com';
